/**
 * Okta Token 服务
 * 处理 OAuth 2.0 授权码交换 access token
 */

import { getOktaConfig } from '../request/config';
import { getHostname } from '../../helpers/oktaUtils';

export interface OktaTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  scope: string;
  id_token?: string;
  refresh_token?: string;
  error?: string;
  error_description?: string;
}

/**
 * 使用授权码交换 access token
 * @param code 授权码
 * @returns Promise<Response | null>
 */
export async function getToken(code: string): Promise<Response | null> {
  const codeVerifier = localStorage.getItem('code_verifier') || '';

  if (codeVerifier) {
    const oktaConfig = getOktaConfig();
    const params = new URLSearchParams();

    params.set('code', code);
    params.set('code_verifier', codeVerifier);
    params.set('grant_type', 'authorization_code');
    params.set('redirect_uri', `${getHostname()}${oktaConfig.redirectURI}`);
    params.set('client_id', oktaConfig.clientId);

    return fetch(`${oktaConfig.host}/v1/token`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Cache-Control': 'no-cache',
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params,
    });
  }

  return null;
}

/**
 * 处理 Okta 登录回调
 * @param code 授权码
 * @returns Promise<{ success: boolean; accessToken?: string; error?: string }>
 */
export async function handleOktaCallback(code: string): Promise<{
  success: boolean;
  accessToken?: string;
  user?: Record<string, unknown>;
  error?: string;
}> {
  try {
    const response = await getToken(code);

    if (!response) {
      // 尝试从 localStorage 获取已存在的 token
      const existingToken = localStorage.getItem('access_token');
      if (existingToken) {
        return { success: true, accessToken: existingToken };
      }
      return { success: false, error: 'No code verifier found' };
    }

    const resp: OktaTokenResponse = await response.json();

    if (resp.error) {
      return { success: false, error: resp.error_description || resp.error };
    }

    const accessToken = resp.access_token;

    if (accessToken) {
      // 保存 token 并清除 code_verifier
      localStorage.setItem('access_token', accessToken);
      localStorage.removeItem('code_verifier');

      return { success: true, accessToken };
    }

    return { success: false, error: 'No access token in response' };
  } catch (error) {
    console.error('Okta callback error:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

export default { getToken, handleOktaCallback };
